<template>
  <div data-app>
    <div
      role="alert"
      v-bind:class="{ show: errors.length }"
      class="alert fade alert-danger"
    >
      <div class="alert-text" v-for="(error, i) in errors" :key="i">
        {{ error }}
      </div>
    </div>
    <v-card>
      <v-card-title>{{ website_name }} Firewall</v-card-title>
      <v-data-table
        v-model="selected"
        :loading="loading"
        :headers="headers"
        :items="rules"
        :search="search"
        :footer-props="{
          itemsPerPageOptions: [5, 10, 50, 100, 250, -1], // -> Add this example
          showFirstLastPage: false,
          firstIcon: 'mdi-arrow-collapse-left',
          lastIcon: 'mdi-arrow-collapse-right',
          prevIcon: 'mdi-chevron-left',
          nextIcon: 'mdi-chevron-right',
        }"
        item-key="id"
        class="elevation-1"
      >
        <template v-slot:item.source="{ item }">
          {{ getSource(item) }}
        </template>
        <template v-slot:top>
          <v-toolbar flat color="white">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
            ></v-text-field>
            <v-spacer></v-spacer>
            <v-dialog v-model="dialog" max-width="500px">
              <template v-slot:activator="{ on }">
                <v-btn color="primary" dark class="mb-2" v-on="on"
                  >New Rule</v-btn
                >
              </template>
              <v-card>
                <v-card-title>
                  <span class="headline">New Rule</span>
                </v-card-title>

                <v-card-text>
                  <v-form ref="newForm" lazy-validation>
                    <v-container>
                      <v-row dense>
                        <v-col cols="12">
                          <v-text-field
                            v-model="editedItem.path"
                            label="Path"
                          />
                        </v-col>
                        <v-col cols="12">
                          <v-text-field
                            v-model="editedItem.user_agent"
                            label="User Agent"
                          />
                        </v-col>
                        <v-col cols="12">
                          <v-text-field
                            v-model="editedItem.ja4_fingerprint"
                            label="JA4 Fingerprint"
                            :rules="ja4Rules"
                          />
                        </v-col>
                        <v-col cols="12" sm="4">
                          <v-select
                            :items="['Network', 'Country']"
                            v-model="editedItem.source_type"
                            label="Source Type"
                            editable
                          />
                        </v-col>
                        <v-col
                          cols="12"
                          sm="8"
                          v-if="editedItem.source_type === 'Network'"
                        >
                          <v-text-field
                            v-model="editedItem.source_net"
                            label="Source Network"
                            :rules="ipRules"
                          />
                        </v-col>
                        <v-col
                          cols="12"
                          sm="8"
                          v-if="editedItem.source_type === 'Country'"
                        >
                          <v-autocomplete
                            :items="countryOptions"
                            v-model="editedItem.source_country"
                            label="Source Country*"
                            item-text="name"
                            item-value="code"
                          />
                        </v-col>
                        <v-col cols="12">
                          <v-select
                            v-model="editedItem.method"
                            :items="methods"
                            label="Method"
                            editable
                            clearable
                          />
                        </v-col>
                        <v-col cols="12">
                          <v-select
                            v-model="editedItem.action_type"
                            :items="['block', 'rate_limit', 'redirect', 'log', 'challenge', 'allow']"
                            label="Action*"
                            @change="onTypeChange($event)"
                            required
                          />
                        </v-col>
                        <v-col
                          v-if="editedItem.action_type === 'rate_limit'"
                          cols="12"
                        >
                          <v-select
                            v-model="editedItem.rate_limiter_id"
                            label="Rate limiter*"
                            :items="rate_limiters"
                            :rules="required"
                            required
                          />
                        </v-col>
                        <v-col
                          v-if="editedItem.action_type === 'redirect'"
                          cols="12"
                        >
                          <v-text-field
                            v-model="editedItem.redirect_url"
                            label="Redirect URL*"
                            :rules="required"
                            required
                          />
                        </v-col>
                        <v-col cols="12">
                          <v-text-field
                            v-model="editedItem.priority"
                            label="Priority*"
                            :rules="[(v) => !!v || 'Must be greater than 0']"
                          />
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-form>
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="blue darken-1" text @click="close"
                    >Cancel</v-btn
                  >
                  <v-btn color="blue darken-1" text @click="save">Save</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon small class="mr-2" @click="deleteItem(item)" v-on="on">mdi-delete</v-icon>
            </template>
            <span>Delete</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import api from "@/common/api.service";
import Errors from "@/common/errors";
import requestMethods from "@/common/config/methods";
import ipAddr from "ip-address";
import { overwrite, getNames, getCode } from "country-list";

// Overwrite country names for better display
overwrite([
  {
    code: "GB",
    name: "United Kingdom",
  },
  {
    code: "TW",
    name: "Taiwan",
  },
  {
    code: "VE",
    name: "Venezuela",
  },
  {
    code: "KP",
    name: "North Korea",
  },
  {
    code: "KR",
    name: "South Korea",
  },
  {
    code: "IR",
    name: "Iran",
  },
  {
    code: "BO",
    name: "Bolivia",
  },
  {
    code: "CD",
    name: "Congo",
  },
  {
    code: "FM",
    name: "Micronesia",
  },
  {
    code: "MD",
    name: "Moldova",
  },
  {
    code: "TZ",
    name: "Tanzania",
  },
]);

export default {
  mounted() {
    let website_id = this.$route.params.website_id;

    api
      .query(`beta/waf/rules?website_id=${website_id}`)
      .then((response) => {
        this.rules = response.data.rules.map((rule) => this.formatRule(rule));
      })
      .catch((error) => this.errors.push(...Errors.extractErrorMessage(error)))
      .finally(() => (this.loading = false));

    this.website_name = this.$store.state.web.website_name;
  },

  data() {
    return {
      selected: [],
      headers: [
        { text: "ID", sortable: false, align: "left", value: "id" },
        { text: "Path", value: "path" },
        { text: "User Agent", value: "user_agent" },
        { text: "JA4 Fingerprint", value: "ja4_fingerprint" },
        { text: "Source", value: "source" },
        { text: "Method", value: "method" },
        { text: "Type", value: "action_type" },
        { text: "Rate Limiter ID", value: "rate_limiter_id" },
        { text: "Priority", value: "priority" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      rules: [],
      errors: [],
      loading: true,
      dialog: false,
      editedItem: {
        website_id: "",
        path: null,
        user_agent: null,
        ja4_fingerprint: null,
        source_type: "Network",
        source_net: null,
        source_country: null,
        method: null,
        action_type: "block",
        rate_limiter_id: null,
        priority: 0,
      },
      defaultItem: {
        website_id: "",
        path: null,
        user_agent: null,
        ja4_fingerprint: null,
        source_type: "Network",
        source_net: null,
        source_country: null,
        method: null,
        action_type: "block",
        rate_limiter_id: null,
        priority: 0,
      },
      search: "",
      methods: requestMethods,
      rate_limiters: null,
      websites: null,
      ipRules: [
        (v) =>
          (!v ||
            (!!v &&
              (new ipAddr.Address4(v).isValid() ||
                new ipAddr.Address6(v).isValid()))) ||
          "IP subnet is invalid",
      ],
      ja4Rules: [
        (v) =>
          !v ||
          /^[tqd](?:13|12|11|10|s3|s2|d1|d2|d3|00)[di][0-9]{4}[0-9A-Za-z]{2}_[0-9a-f]{12}_[0-9a-f]{12}$/.test(
            v
          ) ||
          "JA4 fingerprint is invalid",
      ],
      countryOptions: this.getCountryOptions(),
      required: [(v) => !!v || "Value is required"],
      website_name: "",
    };
  },

  watch: {
    dialog(val) {
      val || this.close();
    },
  },

  methods: {
    getCountryOptions() {
      const countries = getNames().map((name) => ({
        name,
        code: getCode(name),
      }));
      return countries.sort((a, b) => a.name.localeCompare(b.name));
    },

    getSource({ source, source_net, source_country }) {
      if (source_country) {
        const country = this.countryOptions.find(
          (c) => c.code === source_country
        );
        return country ? country.name : source_country;
      }
      return source_net || source;
    },

    formatRule(rule) {
      return {
        ...rule,
        source: this.getSource(rule),
      };
    },

    onTypeChange(event) {
      if (event === "rate_limit" && this.rate_limiters == null) {
        this.rate_limiters_loading = true;
        api
          .query("beta/waf/rate_limiters")
          .then(
            (response) =>
              (this.rate_limiters = response.data.rate_limiters.map((rl) => ({
                value: rl.id,
                text: `${rl.id} - ${rl.max_rps} RPS`,
              })))
          )
          .catch((error) => this.errors.push(...Errors.extractErrorMessage(error)))
          .finally(() => (this.rate_limiters_loading = false));
      }
    },

    deleteItem(item) {
      if (confirm("Are you sure you want to delete this item?")) {
        api.delete("beta/waf/rules/" + item.id).then((response) => {
          const index = this.rules.indexOf(item);
          this.rules.splice(index, 1);
        });
      }
    },

    close() {
      this.dialog = false;
      setTimeout(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      }, 300);
    },

    save() {
      if (!this.$refs.newForm.validate()) return;

      let newRule = {
        customer_website_id: this.$route.params.website_id,
        path: this.editedItem.path,
        user_agent: this.editedItem.user_agent,
        ja4_fingerprint: this.editedItem.ja4_fingerprint || null,
        method: this.editedItem.method,
        action_type: this.editedItem.action_type,
        rate_limiter_id: this.editedItem.rate_limiter_id,
        priority: this.editedItem.priority,
      };

      if (this.editedItem.source_type === "Country") {
        newRule.source_country = this.editedItem.source_country;
      } else {
        newRule.source_net = this.editedItem.source_net;
      }

      if (this.editedItem.type === "rate limit")
        newRule.rate_limiter_id = this.editedItem.rate_limiter_id;

      api
        .post("beta/waf/rules", newRule)
        .then((response) => {
          this.rules = [this.formatRule(response.data), ...this.rules];
        })
        .catch((error) => this.errors.push(...Errors.extractErrorMessage(error)));

      this.close();
    },
  },

  computed: {
    formTitle() {
      return "New Rule";
    },
  },
};
</script>
